import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  CornerDownRight,
  Download,
  FileText,
  Filter,
  Loader2,
  Plus,
  Receipt,
  Search,
} from 'lucide-react'
import { api } from '../lib/api'
import ColumnSortTh from '../components/ui/ColumnSortTh'
import ForecastPanel from '../components/billing/ForecastPanel'
import InvoicesChart from '../components/billing/InvoicesChart'
import PendingPanel from '../components/billing/PendingPanel'
import { useAuth } from '../contexts/AuthContext'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import {
  type InvoiceListItem,
  type InvoiceListResponse,
  type InvoiceSeriesDto,
  type InvoiceStatusT,
  type PendingBillingResponse,
} from '@jr2os/shared'

const PAGE_SIZE = 50

export default function Billing() {
  const { t } = useTranslation('billing')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<InvoiceListResponse | null>(null)
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState(searchParams.get('q') ?? '')
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')
  // La gestoría (CONTABILIDAD) ve las facturas pero no el plan de facturación (Pendientes y Previstas dan 403).
  const canPlan = !(auth.status === 'authenticated' && auth.user.role === 'CONTABILIDAD')
  // «Pendientes»: lo que ya toca facturar (cuotas y hitos vencidos). Se carga siempre para el número de la pestaña.
  const [pending, setPending] = useState<PendingBillingResponse | null>(null)
  const [pendingLoading, setPendingLoading] = useState(false)
  const [pendingError, setPendingError] = useState(false)

  // Borradores sin emitir: no salen en «Emitidas», pero un borrador que queda tras una emisión fallida no puede pasar desapercibido.
  const [draftCount, setDraftCount] = useState(0)

  // '' = Emitidas (por defecto) · 'pending' = Pendientes · 'forecast' = Previstas
  const view = canPlan ? (searchParams.get('view') ?? '') : ''
  const status = (searchParams.get('status') ?? '') as InvoiceStatusT | ''
  const year = searchParams.get('year') ?? ''
  const month = searchParams.get('month') ?? ''
  const seriesId = searchParams.get('seriesId') ?? ''
  const sort = searchParams.get('sort') ?? ''
  const dir: 'asc' | 'desc' = searchParams.get('dir') === 'desc' ? 'desc' : 'asc'
  const clientId = searchParams.get('clientId') ?? ''
  const page = Number(searchParams.get('page') ?? '1')

  const loadDrafts = useCallback(async () => {
    try {
      const r = await api.get<InvoiceListResponse>('/invoices?status=BORRADOR&pageSize=1')
      setDraftCount(r.total)
    } catch {
      setDraftCount(0)
    }
  }, [])

  useEffect(() => {
    void loadDrafts()
  }, [loadDrafts])

  const loadPending = useCallback(async () => {
    if (!canPlan) return
    setPendingLoading(true)
    setPendingError(false)
    try {
      setPending(await api.get<PendingBillingResponse>('/billing/pending'))
    } catch {
      setPendingError(true)
      setPending(null)
    } finally {
      setPendingLoading(false)
    }
  }, [canPlan])

  useEffect(() => {
    void loadPending()
  }, [loadPending])

  const load = useCallback(async () => {
    // Las otras carpetas no usan el listado.
    if (view) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      // «Emitidas» no enseña borradores (con el filtro de estado «Borrador» sí se ven).
      params.set('excludeDrafts', '1')
      if (q.trim()) params.set('q', q.trim())
      if (status) params.set('status', status)
      if (year) params.set('year', year)
      if (month) params.set('month', month)
      if (seriesId) params.set('seriesId', seriesId)
      if (clientId) params.set('clientId', clientId)
      if (sort) {
        params.set('sort', sort)
        params.set('dir', dir)
      }
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<InvoiceListResponse>(`/invoices?${params.toString()}`)
      setData(resp)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadInvoices'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [view, q, status, year, month, seriesId, clientId, sort, dir, page, t])

  useEffect(() => {
    void load()
  }, [load])

  // El gráfico de «Emitidas»: los mismos filtros que el listado, sin la paginación.
  const chartQuery = useMemo(() => {
    const params = new URLSearchParams()
    if (q.trim()) params.set('q', q.trim())
    if (status) params.set('status', status)
    if (year) params.set('year', year)
    if (month) params.set('month', month)
    if (seriesId) params.set('seriesId', seriesId)
    if (clientId) params.set('clientId', clientId)
    return params.toString()
  }, [q, status, year, month, seriesId, clientId])

  useEffect(() => {
    api
      .get<{ items: InvoiceSeriesDto[] }>('/invoice-series')
      .then((r) => setSeries(r.items))
      .catch(() => setSeries([]))
  }, [])

  const updateParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    next.delete('page')
    setSearchParams(next, { replace: true })
  }

  /** Click en una cabecera: sin orden → ascendente → descendente → sin orden (el de siempre: lo último emitido primero). */
  const onSort = (key: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete('page')
    if (sort !== key) {
      next.set('sort', key)
      next.set('dir', 'asc')
    } else if (dir === 'asc') {
      next.set('dir', 'desc')
    } else {
      next.delete('sort')
      next.delete('dir')
    }
    setSearchParams(next, { replace: true })
  }
  const th = (key: string, label: string, align: 'left' | 'right') => (
    <ColumnSortTh
      sortKey={key}
      label={label}
      align={align}
      active={sort === key}
      dir={dir}
      onSort={onSort}
      ariaLabel={t('table.sortBy', { col: label })}
    />
  )

  const onNewInvoice = async () => {
    const def = series.find((s) => s.isDefault) ?? series[0]
    if (!def) {
      setError(t('errors.noSeries'))
      return
    }
    // Necesitamos un cliente — abrimos modal sencillo
    navigate('/billing/new')
  }

  const years = useMemo(() => {
    const now = new Date().getFullYear()
    return [now, now - 1, now - 2]
  }, [])

  // Cada rectificativa, pegada a la factura que anula.
  // Con un orden elegido las filas van EN ESE ORDEN (la rectificativa conserva su etiqueta «rectifica a», pero no se mueve).
  const grouped = useMemo(
    () =>
      sort
        ? (data?.items ?? []).map((inv) => ({ inv, rectifiesNumber: inv.rectifiesNumber ?? null }))
        : groupRectifications(data?.items ?? []),
    [data, sort]
  )

  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    setExporting(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (clientId) params.set('clientId', clientId)
      if (year && month) {
        // Año y mes: ese mes. (El mes solo, sin año, no es un rango: el CSV sale entero.)
        const m = String(month).padStart(2, '0')
        const lastDay = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate()
        params.set('from', `${year}-${m}-01`)
        params.set('to', `${year}-${m}-${String(lastDay).padStart(2, '0')}`)
      } else if (year) {
        params.set('from', `${year}-01-01`)
        params.set('to', `${year}-12-31`)
      }
      const qs = params.toString()
      await downloadFile(`/export/invoices.csv${qs ? `?${qs}` : ''}`, 'facturas.csv')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.exportInvoices'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Receipt size={20} className="text-jr2-700 dark:text-jr2-300" />
            {t('title')}
          </h1>
          {data && !view && (
            <p className="text-xs t-muted">
              {data.total} {data.total !== 1 ? t('invoicesCountPlural') : t('invoicesCountSingular')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onExport()}
            disabled={exporting}
            title={t('exportCsvTitle')}
            className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-jr2-700 dark:hover:text-jr2-200 flex items-center gap-1.5 disabled:opacity-50"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            {t('exportCsv')}
          </button>
          <button
            type="button"
            onClick={() => void onNewInvoice()}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('newInvoice')}
          </button>
        </div>
      </header>

      {/* Carpetas: Emitidas (pasado) · Pendientes (ahora) · Previstas (futuro) */}
      <div className="flex items-center gap-4 border-b border-base">
        {(
          [
            { v: '', label: t('forecast.tabInvoices'), badge: 0 },
            { v: 'pending', label: t('forecast.tabPending'), badge: pending?.count ?? 0 },
            { v: 'forecast', label: t('forecast.tabForecast'), badge: 0 },
          ] as const
        )
          .filter((tab) => canPlan || tab.v === '')
          .map((tab) => (
          <button
            key={tab.v || 'issued'}
            type="button"
            data-testid={`tab-${tab.v || 'issued'}`}
            onClick={() => {
              const next = new URLSearchParams(searchParams)
              if (tab.v) next.set('view', tab.v)
              else next.delete('view')
              setSearchParams(next, { replace: true })
            }}
            className={`px-1 pb-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
              view === tab.v
                ? 'border-jr2-500 text-jr2-500'
                : 'border-transparent t-muted hover:text-jr2-700 dark:hover:text-jr2-200'
            }`}
          >
            {tab.label}
            {tab.badge > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === 'forecast' ? (
        <ForecastPanel />
      ) : view === 'pending' ? (
        <PendingPanel
          data={pending}
          loading={pendingLoading}
          error={pendingError}
          canWrite={canWrite}
          onChanged={() => {
            void loadPending()
            void loadDrafts()
            void load()
          }}
        />
      ) : (
        <>
      {draftCount > 0 && status !== 'BORRADOR' && (
        <div className="surface p-3 text-sm rounded-lg border text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30 flex items-center gap-2 flex-wrap">
          <AlertCircle size={14} />
          <span>{t('draftsNotice', { count: draftCount })}</span>
          <button
            type="button"
            onClick={() => updateParam('status', 'BORRADOR')}
            className="font-semibold underline hover:no-underline"
          >
            {t('draftsNoticeView')}
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50"
          />
        </div>
        <FilterSelect
          label={t('filters.status')}
          value={status}
          onChange={(v) => updateParam('status', v)}
          options={[
            { v: '', l: t('filters.all') },
            ...(['BORRADOR', 'EMITIDA', 'PARCIAL', 'COBRADA', 'ANULADA'] as const).map((s) => ({
              v: s,
              l: t('enums:InvoiceStatus.' + s),
            })),
          ]}
        />
        <FilterSelect
          label={t('filters.year')}
          value={year}
          onChange={(v) => updateParam('year', v)}
          options={[{ v: '', l: t('filters.all') }, ...years.map((y) => ({ v: String(y), l: String(y) }))]}
        />
        <FilterSelect
          label={t('filters.month')}
          value={month}
          onChange={(v) => updateParam('month', v)}
          options={[
            { v: '', l: t('filters.all') },
            ...Array.from({ length: 12 }, (_, m) => ({
              v: String(m + 1),
              l: new Date(Date.UTC(2000, m, 1)).toLocaleDateString(currentLocaleTag(), {
                month: 'long',
                timeZone: 'UTC',
              }),
            })),
          ]}
        />
        <FilterSelect
          label={t('filters.series')}
          value={seriesId}
          onChange={(v) => updateParam('seriesId', v)}
          options={[
            { v: '', l: t('filters.allFemale') },
            ...series.map((s) => ({ v: s.id, l: `${s.prefix} · ${s.name}` })),
          ]}
        />
      </div>

      {/* El gráfico, justo después de los filtros */}
      <InvoicesChart query={chartQuery} />

      {/* Tabla — escritorio/tablet */}
      <div className="surface overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              {th('number', t('table.number'), 'left')}
              {th('date', t('table.date'), 'left')}
              {th('client', t('table.client'), 'left')}
              {th('kind', t('table.kind'), 'left')}
              {th('base', t('table.base'), 'right')}
              {th('total', t('table.total'), 'right')}
              {th('paid', t('table.paid'), 'right')}
              {th('status', t('table.status'), 'left')}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('loading')}
                </td>
              </tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center">
                  <Receipt size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">{t('emptyState')}</p>
                </td>
              </tr>
            )}
            {grouped.map(({ inv, rectifiesNumber }) => (
              <InvoiceRow key={inv.id} inv={inv} rectifiesNumber={rectifiesNumber} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Cards — móvil */}
      <div className="md:hidden space-y-2">
        {loading && (
          <div className="surface p-6 text-center t-muted text-sm">
            <Loader2 size={14} className="inline-block animate-spin mr-2" /> {t('loading')}
          </div>
        )}
        {!loading && data && data.items.length === 0 && (
          <div className="surface p-6 text-center">
            <Receipt size={24} className="mx-auto t-faint mb-2" />
            <p className="t-muted text-sm">{t('emptyState')}</p>
          </div>
        )}
        {!loading &&
          grouped.map(({ inv, rectifiesNumber }) => (
            <InvoiceCard key={inv.id} inv={inv} rectifiesNumber={rectifiesNumber} />
          ))}
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
        </>
      )}
    </div>
  )
}

/**
 * Una fila del listado junto con su papel en la agrupación: `rectifiesNumber` es
 * el número de la factura a la que anula, y solo viene relleno cuando esa
 * factura está justo encima.
 */
interface GroupedInvoice {
  inv: InvoiceListItem
  rectifiesNumber: string | null
}

/**
 * Coloca cada rectificativa inmediatamente debajo de la factura que anula.
 *
 * POR QUÉ: el listado va por fecha, y una rectificativa puede emitirse semanas
 * después de la factura que corrige. Sueltas y separadas, hay que reconstruir a
 * mano qué anula a qué; juntas se lee de un vistazo.
 *
 * NO SE PIERDE NINGUNA FILA: si la factura original no está en la página o la
 * esconde el filtro, la rectificativa se queda en su sitio como una fila normal.
 * Se recorre en profundidad por si algún día se rectifica una rectificativa, y
 * con un conjunto de visitados por si los datos trajeran un ciclo.
 */
function groupRectifications(items: InvoiceListItem[]): GroupedInvoice[] {
  const present = new Set(items.map((i) => i.id))
  const childrenOf = new Map<string, InvoiceListItem[]>()
  for (const inv of items) {
    if (!inv.rectifiesId || !present.has(inv.rectifiesId)) continue
    const list = childrenOf.get(inv.rectifiesId)
    if (list) list.push(inv)
    else childrenOf.set(inv.rectifiesId, [inv])
  }

  const attached = new Set<string>()
  for (const list of childrenOf.values()) for (const c of list) attached.add(c.id)

  const out: GroupedInvoice[] = []
  const seen = new Set<string>()
  const emit = (inv: InvoiceListItem, rectifiesNumber: string | null): void => {
    if (seen.has(inv.id)) return
    seen.add(inv.id)
    out.push({ inv, rectifiesNumber })
    for (const child of childrenOf.get(inv.id) ?? []) emit(child, inv.number ?? null)
  }
  for (const inv of items) {
    if (attached.has(inv.id)) continue // sale pegada a la suya
    emit(inv, null)
  }
  return out
}

function InvoiceRow({ inv, rectifiesNumber }: GroupedInvoice) {
  const { t } = useTranslation('billing')
  const pending = inv.totalCents - inv.paidCents
  const annulled = inv.status === 'ANULADA'
  return (
    <tr
      className={[
        'border-t border-base/40 hover:bg-hover',
        // La anulada tiñe más: es la que hay que descartar al leer. La
        // rectificativa lleva un tono suave para que el par se vea como un bloque.
        annulled ? 'bg-rose-500/[0.09]' : rectifiesNumber ? 'bg-rose-500/[0.04]' : '',
      ].join(' ')}
    >
      <td className="px-3 py-2 font-mono text-jr2-700 dark:text-jr2-300">
        {rectifiesNumber ? (
          <span className="flex items-center gap-1.5">
            <CornerDownRight size={12} className="text-rose-500/70 shrink-0" />
            <span>{inv.number ?? '—'}</span>
          </span>
        ) : (
          inv.number ?? '—'
        )}
        {rectifiesNumber && (
          <div className="pl-[18px] text-[10px] font-sans t-faint">
            {t('rectifiesShort', { number: rectifiesNumber })}
          </div>
        )}
      </td>
      <td className="px-3 py-2 t-secondary">
        {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString(currentLocaleTag()) : '—'}
      </td>
      <td className="px-3 py-2 t-primary">
        <Link to={`/billing/${inv.id}`} className="hover:underline">
          {inv.clientDisplayName}
        </Link>
      </td>
      <td className="px-3 py-2 text-xs t-faint">{t('enums:InvoiceKind.' + inv.kind)}</td>
      <td className="px-3 py-2 text-right t-secondary">{formatCents(inv.subtotalCents)}</td>
      <td className="px-3 py-2 text-right t-primary font-semibold">{formatCents(inv.totalCents)}</td>
      <td className="px-3 py-2 text-right">
        {inv.paidCents > 0 ? (
          <span className="text-emerald-700 dark:text-emerald-300">{formatCents(inv.paidCents)}</span>
        ) : (
          <span className="t-faint">—</span>
        )}
        {/* Solo en PARCIAL: en EMITIDA el pendiente = total y ya lo dice el estado. */}
        {pending > 0 && inv.status === 'PARCIAL' && (
          <div className="text-[10px] text-rose-700 dark:text-rose-300">{formatCents(pending)} {t('pendingShort')}</div>
        )}
      </td>
      <td className="px-3 py-2">
        <StatusPill status={inv.status} />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <Link
            to={`/billing/${inv.id}`}
            className="px-2 py-1 rounded text-xs t-secondary hover:bg-hover"
          >
            {t('open')}
          </Link>
          {inv.status !== 'BORRADOR' && (
            <a
              href={`/api/invoices/${inv.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
              title={t('viewPdf')}
            >
              <FileText size={13} />
            </a>
          )}
        </div>
      </td>
    </tr>
  )
}

function InvoiceCard({ inv, rectifiesNumber }: GroupedInvoice) {
  const { t } = useTranslation('billing')
  const pending = inv.totalCents - inv.paidCents
  const annulled = inv.status === 'ANULADA'
  return (
    <Link
      to={`/billing/${inv.id}`}
      className={[
        'block surface p-3 hover:bg-hover/50 transition-colors',
        annulled ? 'bg-rose-500/[0.09]' : '',
        // Sangrada bajo la que anula, igual que en la tabla.
        rectifiesNumber ? 'ml-4 bg-rose-500/[0.04]' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span className="font-mono text-xs text-jr2-700 dark:text-jr2-300 flex items-center gap-1.5">
          {rectifiesNumber && <CornerDownRight size={11} className="text-rose-500/70 shrink-0" />}
          {inv.number ?? '—'}
        </span>
        <StatusPill status={inv.status} />
      </div>
      {rectifiesNumber && (
        <div className="text-[10px] t-faint -mt-1 mb-1">
          {t('rectifiesShort', { number: rectifiesNumber })}
        </div>
      )}
      <div className="t-primary text-sm font-medium truncate">{inv.clientDisplayName}</div>
      <div className="flex items-center justify-between text-[11px] t-muted mt-1">
        <span>{t('enums:InvoiceKind.' + inv.kind)}</span>
        <span>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString(currentLocaleTag()) : '—'}</span>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-base/40">
        <div className="text-[11px] t-faint">{t('table.total')}</div>
        <div className="font-semibold t-primary">{formatCents(inv.totalCents)}</div>
      </div>
      {inv.paidCents > 0 && (
        <div className="flex items-center justify-between text-[11px] mt-1">
          <span className="t-muted">{t('table.paid')}</span>
          <span className="text-emerald-700 dark:text-emerald-300">{formatCents(inv.paidCents)}</span>
        </div>
      )}
      {pending > 0 && inv.status === 'PARCIAL' && (
        <div className="flex items-center justify-between text-[11px] mt-0.5">
          <span className="t-muted">{t('pending')}</span>
          <span className="text-rose-700 dark:text-rose-300">{formatCents(pending)}</span>
        </div>
      )}
    </Link>
  )
}

function StatusPill({ status }: { status: InvoiceStatusT }) {
  const { t } = useTranslation()
  const STYLES: Record<InvoiceStatusT, string> = {
    BORRADOR: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
    EMITIDA: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
    PARCIAL: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    COBRADA: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    ANULADA: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
  }
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {t('enums:InvoiceStatus.' + status)}
    </span>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      <Filter size={12} className="t-faint" /> {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}
